import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchListPage } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import type { CatalogItem, GraphListItem } from "../types/navigator";

export interface UseListDataOptions<T extends CatalogItem> {
  listId: string;
  mapper: (raw: GraphListItem) => T;
  /** Client-side text search across all string fields of mapped items. */
  search: string;
  /** Server-side `$filter` clauses by SharePoint field name. Multiple values
   * per field are combined with `or`; different fields are combined with
   * `and`. */
  filters: Record<string, string[]>;
  /** Items per Graph request while paging through the whole list. */
  pageSize?: number;
}

export interface UseListDataResult<T extends CatalogItem> {
  /** Search-matched items the UI should display. */
  items: T[];
  loading: boolean;
  error: string | null;
  /** Refetch from scratch, clearing the list while it loads. */
  reset: () => void;
  /** Refetch and swap the result in without clearing what's on screen —
   *  for after an edit, so the table updates in place. */
  refresh: () => void;
}

// Safety ceiling on how much of a list we'll pull. These lists hold tens of
// items today; this only guards against a runaway list, not normal growth.
const MAX_ITEMS = 5000;

function itemSearchHaystack(item: CatalogItem): string {
  const parts: string[] = [];
  for (const value of Object.values(item)) {
    if (typeof value === "string") parts.push(value);
  }
  // Walk into raw fields for anything we didn't surface explicitly.
  if (item.raw && typeof item.raw === "object") {
    for (const v of Object.values(item.raw)) {
      if (typeof v === "string") parts.push(v);
    }
  }
  return parts.join("  ").toLowerCase();
}

function matchesSearch(item: CatalogItem, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const hay = itemSearchHaystack(item);
  return terms.every((t) => hay.includes(t));
}

/**
 * Loads the WHOLE list, following every Graph page before showing anything.
 *
 * Sorting, search and the filter dropdowns all run client-side, so they're
 * only correct when every item is present. Loading one page at a time hid
 * every item past the first page (and left its region/submitter out of the
 * filters), and two code paths fetching "the next page" at once appended
 * the same page twice. One complete fetch removes both problems.
 */
export function useListData<T extends CatalogItem>(
  opts: UseListDataOptions<T>
): UseListDataResult<T> {
  const getToken = useGraphToken();
  const [rawItems, setRawItems] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const requestIdRef = useRef(0);
  const silentRef = useRef(false);

  const filtersKey = JSON.stringify(opts.filters);
  const searchTerms = useMemo(() => {
    const q = opts.search.trim().toLowerCase();
    if (!q) return [] as string[];
    return q.split(/\s+/).filter(Boolean);
  }, [opts.search]);

  // Search is NOT a dependency: it's applied client-side, no refetch needed.
  useEffect(() => {
    const myId = ++requestIdRef.current;
    let cancelled = false;
    const silent = silentRef.current;
    silentRef.current = false;
    (async () => {
      try {
        if (!silent) {
          setLoading(true);
          setError(null);
          setRawItems([]);
        }
        const token = await getToken();
        const byId = new Map<string, T>();
        let nextLink: string | undefined;
        do {
          const result = await fetchListPage({
            token,
            listId: opts.listId,
            pageSize: opts.pageSize ?? 200,
            filters: opts.filters,
            nextLink,
          });
          if (cancelled || requestIdRef.current !== myId) return;
          for (const raw of result.items) {
            const item = opts.mapper(raw);
            byId.set(item.id, item);
          }
          nextLink = result.nextLink;
        } while (nextLink && byId.size < MAX_ITEMS);

        if (cancelled || requestIdRef.current !== myId) return;
        setRawItems(Array.from(byId.values()));
        if (silent) setError(null);
      } catch (e) {
        // A failed silent refresh keeps the existing rows rather than
        // replacing a working table with an error state.
        if (!cancelled && requestIdRef.current === myId && !silent) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled && requestIdRef.current === myId) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.listId, filtersKey, opts.pageSize, reloadKey, getToken]);

  const filteredItems = useMemo(
    () => rawItems.filter((it) => matchesSearch(it, searchTerms)),
    [rawItems, searchTerms]
  );

  const reset = useCallback(() => setReloadKey((k) => k + 1), []);
  const refresh = useCallback(() => {
    silentRef.current = true;
    setReloadKey((k) => k + 1);
  }, []);

  return { items: filteredItems, loading, error, reset, refresh };
}
